import api from './client';

export interface ServiceBoxFieldDiff {
  field: "reception" | "restitution";
  old: string | null;
  new: string | null;
}

export type ServiceBoxRdvState = "new" | "existing" | "modified";

export interface ServiceBoxRecentRdv {
  dossier_id: string;
  num: string;
  client_name: string;
  /** Coordonnées portées par la fiche RDV ServiceBox — remplissent le contact Carvis
   *  à l'import (elles étaient scrapées puis jetées avant 09/2026). */
  client_phone?: string | null;
  client_email?: string | null;
  client_adresse?: string | null;
  client_code_postal?: string | null;
  client_ville?: string | null;
  client_dms_id?: string | null;
  plate: string;
  modele: string;
  /** vehiculeDto.dateMiseCirculation (« 20190601 ») — complète la MEC Carvis à l'import. */
  vehicle_mec?: string | null;
  reception_date: string | null;
  reception_heure: string | null;
  reception_colonne: string | null;
  reception_personnel: string | null;
  restitution_date: string | null;
  restitution_heure: string | null;
  restitution_colonne: string | null;
  restitution_personnel: string | null;
  interventions: string[];
  status: string;
  exists_in_heric: boolean;
  heric_ordre_id: string | null;
  state: ServiceBoxRdvState;
  changed_fields: ServiceBoxFieldDiff[];
}

export interface ServiceBoxRecentRdvsResponse {
  total: number;
  new_count: number;
  existing_count: number;
  modified_count: number;
  rdvs: ServiceBoxRecentRdv[];
}

export interface ServiceBoxImportRdvResponse {
  ordre_id: string;
  dossier_id: string;
  message: string;
}

export interface ServiceBoxBatchImportResult {
  dossier_id: string;
  ordre_id: string | null;
  num: string;
  success: boolean;
  message: string;
  jobs_created: number;
}

export interface ServiceBoxBatchImportResponse {
  total: number;
  success_count: number;
  error_count: number;
  total_jobs: number;
  results: ServiceBoxBatchImportResult[];
}

export interface ServiceBoxUpdateRdvResponse {
  ordre_id: string;
  updated_fields: string[];
  jobs_resynced: number;
  message: string;
}

export interface ServiceBoxBatchUpdateResult {
  ordre_id: string;
  dossier_id: string;
  num: string;
  success: boolean;
  message: string;
  updated_fields: string[];
  jobs_resynced: number;
}

export interface ServiceBoxBatchUpdateResponse {
  total: number;
  success_count: number;
  error_count: number;
  total_jobs_resynced: number;
  results: ServiceBoxBatchUpdateResult[];
}

function rdvToPayload(rdv: ServiceBoxRecentRdv) {
  return {
    dossier_id: rdv.dossier_id,
    num: rdv.num,
    client_name: rdv.client_name,
    client_phone: rdv.client_phone,
    client_email: rdv.client_email,
    client_adresse: rdv.client_adresse,
    client_code_postal: rdv.client_code_postal,
    client_ville: rdv.client_ville,
    client_dms_id: rdv.client_dms_id,
    plate: rdv.plate,
    modele: rdv.modele,
    vehicle_mec: rdv.vehicle_mec,
    reception_date: rdv.reception_date,
    reception_heure: rdv.reception_heure,
    reception_colonne: rdv.reception_colonne,
    reception_personnel: rdv.reception_personnel,
    restitution_date: rdv.restitution_date,
    restitution_heure: rdv.restitution_heure,
    restitution_colonne: rdv.restitution_colonne,
    restitution_personnel: rdv.restitution_personnel,
    interventions: rdv.interventions,
    status: rdv.status,
  };
}

/** One work line as ServiceBox spells it on the RDV. */
export interface ServiceBoxWorkLine {
  nom: string;
  duree: string;
}

/** A Carvis intervention as it would be sent to ServiceBox. */
export interface CarvisWorkLine {
  id: string;
  nom: string;
  duree: number | null;
  forfait_code: string;
  type: string;
}

/** What ServiceBox already holds on an ordre's RDV, and what Carvis has in addition. */
export interface ServiceBoxInterventionsPlan {
  success: boolean;
  error?: string;
  rdv_id?: string;
  or_number?: string | null;
  /** Carvis has an OR number the rendez-vous does not yet: Alpha is still answering, and
   *  its answer rewrites the dossier's work lines — anything pushed now is wiped. */
  dms_in_flight?: boolean;
  servicebox_lines?: ServiceBoxWorkLine[];
  to_add?: CarvisWorkLine[];
  already_on_servicebox?: CarvisWorkLine[];
  not_selected?: CarvisWorkLine[];
  /** Slots and desks have no button: a Carvis edit queues its own push to ServiceBox. */
  slots?: { auto: boolean; pending: boolean; last?: string | null };
  /** ServiceBox basket Alpha builds the OR from; provisional = no dossier there yet. */
  dossier_id?: string | null;
  dossier_provisional?: boolean;
}

export interface ServiceBoxRefreshOrResult {
  success: boolean;
  error?: string;
  /** null = ServiceBox has no OR on this RDV yet (not transferred to Alpha). */
  or_number?: string | null;
  updated?: boolean;
  detail?: string;
  /** Dossier ServiceBox du RDV (null = RDV sans dossier : rien à transférer vers Alpha). */
  dossier_id?: string | null;
}

export interface ServiceBoxPushInterventionsResult {
  success: boolean;
  error?: string;
  unchanged?: boolean;
  detail?: string;
  rdv_id?: string;
  added?: string[];
  missing?: string[];
  lost?: string[];
  warnings?: string;
  /** Set when the RDV already carries an OR: the line landed on ServiceBox, not in Alpha. */
  or_warning?: string;
  /** Set when a pushed line carried a forfait code: the RDV form files it as Standard. */
  forfait_warning?: string;
  servicebox_lines_after?: ServiceBoxWorkLine[];
}

/** Where an ordre was created, and on what stored evidence. */
export interface OrdreOrigin {
  where: "servicebox" | "carvis";
  label: string;
  at?: string | null;
  evidence?: Record<string, any>;
}

/** One field, as Carvis holds it next to the last value ServiceBox showed. */
export interface SyncStateRow {
  field: string;
  carvis?: string | null;
  servicebox?: string | null;
  in_sync: boolean;
  /** No ServiceBox baseline yet: until there is one, ServiceBox stays the reference. */
  no_baseline: boolean;
}

export interface SyncHistoryEvent {
  at?: string | null;
  kind: string;
  direction: string;
  title: string;
  detail?: string | null;
  error?: string | null;
  success?: boolean | null;
  attempt?: number | null;
  written?: Record<string, string> | null;
  /** The field or document this line was read from — nothing here is inferred. */
  source: string;
}

export interface OrdreSyncHistory {
  success: boolean;
  error?: string;
  origin?: OrdreOrigin;
  identifiers?: Record<string, string | null>;
  state?: SyncStateRow[];
  events?: SyncHistoryEvent[];
  /** What this timeline cannot see. Shown, not hidden: silence is not proof. */
  gaps?: string[];
}

export const serviceboxSyncApi = {
  getRecentRdvs: async (days: number = 14, direction: string = "past"): Promise<ServiceBoxRecentRdvsResponse> => {
    const { data } = await api.get('/servicebox/recent-rdvs', { params: { days, direction } });
    return data;
  },

  importRdv: async (rdv: ServiceBoxRecentRdv): Promise<ServiceBoxImportRdvResponse> => {
    const { data } = await api.post('/servicebox/import-rdv', rdvToPayload(rdv));
    return data;
  },

  batchImport: async (rdvs: ServiceBoxRecentRdv[]): Promise<ServiceBoxBatchImportResponse> => {
    const { data } = await api.post('/servicebox/batch-import', {
      rdvs: rdvs.map(rdvToPayload),
    }, { timeout: 300000 }); // 5 min — atelier scraping is slow
    return data;
  },

  updateRdv: async (
    rdv: ServiceBoxRecentRdv,
    reSyncAtelier: boolean = false,
  ): Promise<ServiceBoxUpdateRdvResponse> => {
    if (!rdv.heric_ordre_id) throw new Error("RDV sans ordre Heric associé");
    const { data } = await api.post('/servicebox/update-rdv', {
      ordre_id: rdv.heric_ordre_id,
      rdv: rdvToPayload(rdv),
      re_sync_atelier: reSyncAtelier,
    }, { timeout: reSyncAtelier ? 300000 : 60000 });
    return data;
  },

  batchUpdate: async (
    rdvs: ServiceBoxRecentRdv[],
    reSyncAtelier: boolean = false,
  ): Promise<ServiceBoxBatchUpdateResponse> => {
    const payload = rdvs
      .filter((r) => r.heric_ordre_id)
      .map((r) => ({
        ordre_id: r.heric_ordre_id!,
        rdv: rdvToPayload(r),
        re_sync_atelier: reSyncAtelier,
      }));
    const { data } = await api.post('/servicebox/batch-update', {
      rdvs: payload,
    }, { timeout: 300000 });
    return data;
  },

  /** Read-only: the RDV's current work lines, and which Carvis interventions are missing
   *  from them. Opens the RDV form on ServiceBox, so allow for a slow answer. */
  getInterventionsPlan: async (ordreId: string): Promise<ServiceBoxInterventionsPlan> => {
    const { data } = await api.get(`/servicebox/ordres/${ordreId}/interventions`, { timeout: 60000 });
    return data;
  },

  /** Append interventions to the RDV ServiceBox already holds. NOT a transfer: a transfer
   *  creates a RDV, and asking for one on an ordre already there produces a duplicate. */
  pushInterventions: async (
    ordreId: string,
    interventionIds: string[],
  ): Promise<ServiceBoxPushInterventionsResult> => {
    const { data } = await api.post(
      `/servicebox/ordres/${ordreId}/push-interventions`,
      { intervention_ids: interventionIds },
      { timeout: 120000 }, // load the form, post it, re-read the RDV to verify
    );
    return data;
  },

  /** Read back the numéro d'OR ServiceBox shows, once the garage has transferred the
   *  dossier to Alpha itself. Carvis never creates the OR — Alpha prints the priced CT
   *  the moment it does — so the number always arrives this way. */
  refreshOrNumber: async (ordreId: string): Promise<ServiceBoxRefreshOrResult> => {
    const { data } = await api.post(`/servicebox/ordres/${ordreId}/refresh-or`, {}, { timeout: 60000 });
    return data;
  },

  /** Origin + recorded ServiceBox exchanges for one ordre. Reads Firestore only. */
  getSyncHistory: async (ordreId: string): Promise<OrdreSyncHistory> => {
    const { data } = await api.get(`/servicebox/ordres/${ordreId}/sync-history`);
    return data;
  },
};

export interface ServiceBoxAuthError {
  reason: string;
  username: string;
  at: string;
}

/** Connexion de Carvis à ServiceBox refusée (mot de passe expiré…) — ServiceBoxAuthModal. */
export const serviceboxAuthApi = {
  status: async (): Promise<ServiceBoxAuthError | null> => {
    const { data } = await api.get('/servicebox/auth-status');
    return data?.error || null;
  },
  saveCredentials: async (username: string, password: string): Promise<void> => {
    await api.post('/servicebox/credentials', { username, password }, { timeout: 60000 });
  },
};
